import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { User, Stethoscope, FileText, Printer, Edit } from 'lucide-react';
import { encounterService } from '../api/encounters';
import { prescriptionService } from '../api/prescriptions';
import { orderService } from '../api/orders';
import { patientService, doctorService } from '../../../lib/services';
import { printPrescription } from '../utils/prescriptionPdf';
import toast from 'react-hot-toast';
import type { Encounter, Prescription, Order } from '../types';
import type { Patient, Doctor } from '../../../lib/supabase';

export default function ConsultationView() {
  const { id } = useParams<{ id: string }>();
  const [encounter, setEncounter] = useState<Encounter | null>(null);
  const [prescription, setPrescription] = useState<Prescription | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [doctor, setDoctor] = useState<Doctor | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    if (!id) return;
    
    try {
      const encounterData = await encounterService.getById(id);
      setEncounter(encounterData);

      const [prescriptionData, ordersData, patientData, doctorData] = await Promise.all([
        prescriptionService.getByEncounter(id),
        orderService.getByEncounter(id),
        patientService.getById(encounterData.patientId),
        doctorService.getById(encounterData.doctorId),
      ]);

      setPrescription(prescriptionData);
      setOrders(ordersData);
      setPatient(patientData);
      setDoctor(doctorData);
    } catch (error) {
      console.error('Failed to load consultation data:', error);
      toast.error('Failed to load consultation');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintPrescription = () => {
    if (prescription && encounter) {
      const encounterWithPatient = {
        ...encounter,
        patient,
        doctor,
      };
      printPrescription(prescription, encounterWithPatient);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!encounter) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">Consultation not found</h2>
        <Link to="/consultations" className="text-blue-600 hover:text-blue-800 mt-2 inline-block">
          Back to Consultations
        </Link>
      </div>
    );
  }

  return (
    <section className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Consultation Details</h1>
          <p className="text-sm text-neutral-600">
            {new Date(encounter.dateTime).toLocaleDateString()} at {new Date(encounter.dateTime).toLocaleTimeString()}
          </p>
        </div>
        <div className="flex space-x-3">
          {prescription && (
            <button
              onClick={handlePrintPrescription}
              className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-200"
            >
              <Printer className="w-4 h-4" />
              <span>Print Prescription</span>
            </button>
          )}
          <Link
            to={`/consultations/${id}/edit`}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            <Edit className="w-4 h-4" />
            <span>Edit</span>
          </Link>
        </div>
      </header>

      {/* Patient Information */}
      {patient && (
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <User className="w-5 h-5 mr-2" />
            Patient Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-700">Name</p>
              <p className="text-sm text-gray-900">{patient.name}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Email</p>
              <p className="text-sm text-gray-900">{patient.email || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Phone</p>
              <p className="text-sm text-gray-900">{patient.phone || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Date of Birth</p>
              <p className="text-sm text-gray-900">
                {patient.date_of_birth ? new Date(patient.date_of_birth).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Doctor Information */}
      {doctor && (
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Stethoscope className="w-5 h-5 mr-2" />
            Doctor Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-700">Name</p>
              <p className="text-sm text-gray-900">{doctor.name}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Specialization</p>
              <p className="text-sm text-gray-900">{doctor.specialization || 'N/A'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Vitals */}
      {encounter.vitals && (
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold mb-4">Vitals</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {encounter.vitals.bp && (
              <div>
                <p className="text-sm font-medium text-gray-700">Blood Pressure</p>
                <p className="text-sm text-gray-900">{encounter.vitals.bp}</p>
              </div>
            )}
            {encounter.vitals.hr && (
              <div>
                <p className="text-sm font-medium text-gray-700">Heart Rate</p>
                <p className="text-sm text-gray-900">{encounter.vitals.hr} bpm</p>
              </div>
            )}
            {encounter.vitals.temp && (
              <div>
                <p className="text-sm font-medium text-gray-700">Temperature</p>
                <p className="text-sm text-gray-900">{encounter.vitals.temp}°F</p>
              </div>
            )}
            {encounter.vitals.spo2 && (
              <div>
                <p className="text-sm font-medium text-gray-700">SpO2</p>
                <p className="text-sm text-gray-900">{encounter.vitals.spo2}%</p>
              </div>
            )}
            {encounter.vitals.bmi && (
              <div>
                <p className="text-sm font-medium text-gray-700">BMI</p>
                <p className="text-sm text-gray-900">{encounter.vitals.bmi}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Clinical Information */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-semibold mb-4">Clinical Information</h3>
        <div className="space-y-4">
          {encounter.complaints && (
            <div>
              <p className="text-sm font-medium text-gray-700">Chief Complaints</p>
              <p className="text-sm text-gray-900 mt-1">{encounter.complaints}</p>
            </div>
          )}
          {encounter.notes && (
            <div>
              <p className="text-sm font-medium text-gray-700">Clinical Notes</p>
              <p className="text-sm text-gray-900 mt-1">{encounter.notes}</p>
            </div>
          )}
          {encounter.diagnosis && encounter.diagnosis.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-700">Diagnosis</p>
              <div className="mt-1 space-y-1">
                {encounter.diagnosis.map((diag, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <span className="text-sm text-gray-900">{diag.label}</span>
                    {diag.code && (
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {diag.code}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Prescription */}
      {prescription && (
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              Prescription
            </h3>
            <Link
              to={`/prescriptions/${prescription.id}/print`}
              className="text-blue-600 hover:text-blue-800 text-sm"
            >
              View Print Version
            </Link>
          </div>
          <div className="space-y-3">
            {prescription.items.map((item, index) => (
              <div key={index} className="border-l-4 border-blue-200 pl-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900">{item.nameOrMedicineId}</h4>
                  <span className="text-sm text-gray-500">{item.route}</span>
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  <span className="mr-4">Dose: {item.dose}</span>
                  <span className="mr-4">Frequency: {item.frequency}</span>
                  <span>Duration: {item.duration}</span>
                </div>
                {item.instructions && (
                  <p className="text-sm text-gray-600 mt-1 italic">{item.instructions}</p>
                )}
              </div>
            ))}
          </div>
          {prescription.signedBy && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm text-gray-600">Signed by: {prescription.signedBy}</p>
            </div>
          )}
        </div>
      )}

      {/* Orders */}
      {orders.length > 0 && (
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-semibold mb-4">Orders</h3>
          <div className="space-y-3">
            {orders.map((order) => (
              <div key={order.id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900 capitalize">{order.type} Tests</h4>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      order.status === 'completed' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {order.status}
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      order.priority === 'urgent' 
                        ? 'bg-red-100 text-red-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {order.priority}
                    </span>
                  </div>
                </div>
                <div className="space-y-1">
                  {order.items.map((item, index) => (
                    <div key={index} className="text-sm text-gray-600">
                      {item.code && <span className="font-mono mr-2">{item.code}</span>}
                      {item.name}
                    </div>
                  ))}
                </div>
                {order.notes && (
                  <p className="text-sm text-gray-600 mt-2 italic">{order.notes}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
